import {
  BadgePill,
  BrandfolderHexColors,
  ButtonLooks,
  FontIcons,
  IconButton,
  StandardTextfield
} from '@brandfolder/react';
import { t } from '@lingui/macro';
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

import { UseFetchOptions, useFetch } from '@api/ApiHelper';
import { Locale } from '@components/common/language_menu/languagesMap';

import { ManageTagsData, TagCountResponse, updateVisibleTagsAfterRename } from './helpers';

export interface TagNameProps {
  autoGenerated: boolean
  setTags: SetStateDispatch<TagCountResponse>;
  tagName: string;
  tags: TagCountResponse;
  ugtLocale: Locale;
}

export const TagName: FunctionComponent<TagNameProps> = ({ autoGenerated, setTags, tagName, tags, ugtLocale }) => {
  const [editMode, setEditMode] = useState(false);
  const [newTagName, setNewTagName] = useState(tagName);
  const inputRef = useRef(null);

  const putOptions: UseFetchOptions = {
    url: '/api/v4/bulk_actions/assets/manage_tags',
    method: 'PUT',
    params: {
      ugt_locale: ugtLocale // eslint-disable-line @typescript-eslint/naming-convention
    },
    fetchOnMount: false
  };
  const bulkTagsPut = useFetch(putOptions);

  useEffect(() => {
    setNewTagName(tagName);
  }, [tagName]);

  useEffect(() => {
    if (bulkTagsPut.response) {
      updateVisibleTagsAfterRename(tags, setTags, tagName, newTagName);
      setEditMode(false);
    }
  }, [bulkTagsPut.response]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (editMode) {
      inputRef.current?.focus();
    }
  }, [editMode]);

  const renameTag = (): void => {
    if (newTagName !== tagName) {
      bulkTagsPut.fetch({
        ...putOptions,
        body: {
          data: {
            /* eslint-disable @typescript-eslint/naming-convention */
            manage_tags: {
              management_action: 'update',
              brandfolder_key: BFG.resource?.key,
              old_name: tagName,
              new_name: newTagName,
              auto_generated: autoGenerated,
            }
            /* eslint-enable @typescript-eslint/naming-convention */
          } satisfies ManageTagsData
        }
      });
    } else {
      setEditMode(false);
    }
  };

  let ugtLocaleParamString = '';
  if (BFG.locales?.ugtLocaleDefault && ugtLocale !== BFG.locales.ugtLocaleDefault) {
    ugtLocaleParamString = `&ugt_locale=${ugtLocale}`;
  }
  const searchLink = `/${BFG.resource.slug}?q=tags.strict:"${encodeURIComponent(tagName)}"${ugtLocaleParamString}`;
  return (
    <>
      {editMode && (
        <span className="tag-name__container tag-name__container--edit">
          <StandardTextfield
            ref={inputRef}
            error={!!bulkTagsPut.error && t`Oops! Something went wrong.`}
            id={`tag-input-${tagName}`}
            label={t`Rename tag`}
            onChange={(e): void => { setNewTagName(e.target.value); }}
            onKeyUp={(e): void => { if (e.key === 'Enter') renameTag(); }}
            value={newTagName}
          />
          <span className="tag-name__button-container">
            <IconButton
              icon={FontIcons.CheckMark}
              label={t`Rename tag`}
              loading={bulkTagsPut.loading}
              look={ButtonLooks.Tertiary}
              onClick={(): void => { renameTag(); }}
              />
            <IconButton
              disabled={bulkTagsPut.loading}
              icon={FontIcons.Close}
              label={t`Cancel`}
              look={ButtonLooks.Tertiary}
              onClick={(): void => { setEditMode(false); }}
            />
          </span>
        </span>
      )}
      {!editMode && (
        <span className="tag-name__container">
          <a href={searchLink} rel="noopener noreferrer" target="_blank">
            <BadgePill
              backgroundColor={BrandfolderHexColors.White}
              borderColor={BrandfolderHexColors.GrayMedium}
              className={`tag-name ${autoGenerated ? 'auto-generated' : ''}`}
            >
              {tagName}
            </BadgePill>
          </a>
          <IconButton
            icon={FontIcons.Edit}
            label={t`Rename tag`}
            look={ButtonLooks.Tertiary}
            onClick={(): void => { setEditMode(true); }}
          />
        </span>
      )}
    </>
  );
};
